<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="visible" class="donation-modal-container">
      <div class="donation-overlay" @click="close" aria-label="关闭打赏对话框"></div>
      <div class="donation-modal" role="dialog" aria-modal="true" aria-label="赞赏支持" @keydown.esc="close">
        <div class="modal-header">
          <h3 class="modal-title"><i class="fa-solid fa-donate" style="color: #e74c3c;"></i> 赞赏支持</h3>
          <button class="close-btn" @click="close" aria-label="关闭">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body" v-if="!qrCodeUrl">
          <p class="desc-text">您的打赏是对我们最大的支持与鼓励，打赏资金将用于服务器运维与站点日常维护！</p>

          <!-- 预设金额选择 -->
          <div class="amount-title">选择打赏金额</div>
          <div class="amount-grid">
            <button 
              v-for="amt in presetAmounts" 
              :key="amt" 
              class="amount-card" 
              :class="{ active: amount === amt && !customActive }"
              @click="selectAmount(amt)"
            >
              {{ amt }} 元
            </button>
            <button 
              class="amount-card custom-btn" 
              :class="{ active: customActive }"
              @click="selectCustom"
            >
              自定义
            </button>
          </div>

          <!-- 自定义金额输入 -->
          <div v-if="customActive" class="custom-input-container">
            <input 
              v-model.number="customAmount" 
              type="number" 
              class="custom-input" 
              placeholder="请输入金额 (元)"
              min="0.1" 
              step="0.1"
            />
          </div>

          <!-- 支付方式选择 -->
          <div class="pay-title">选择支付方式</div>
          <div class="pay-methods">
            <button 
              class="pay-btn alipay" 
              :class="{ active: paymentMethod === 'alipay' }"
              @click="paymentMethod = 'alipay'"
            >
              <i class="fa-brands fa-alipay"></i> 支付宝支付 (手动对账)
            </button>
            <button 
              class="pay-btn wechat" 
              :class="{ active: paymentMethod === 'wechat' }"
              @click="paymentMethod = 'wechat'"
            >
              <i class="fa-brands fa-weixin"></i> 微信支付 (手动对账)
            </button>
          </div>

          <!-- 留言 -->
          <div class="fields-container">
            <div class="field-item">
              <label class="field-label">打赏留言 (选填)</label>
              <input v-model="message" type="text" class="field-input" placeholder="写句鼓励的话吧..." maxlength="100"/>
            </div>
          </div>

          <div class="action-row">
            <button class="cancel-btn" @click="close">取消</button>
            <button class="confirm-btn" :disabled="isSubmitting || !isValidAmount" @click="createDonation">
              <i class="fa-solid fa-qrcode"></i> {{ isSubmitting ? '正在生成二维码...' : '生成付款码' }}
            </button>
          </div>
        </div>

        <!-- 展示付款二维码 -->
        <div class="modal-body qr-body" v-else>
          <div class="qr-container">
            <img :src="renderedQrUrl" alt="付款二维码" class="qr-image" />
          </div>
          
          <div class="qr-info">
            <div class="qr-amount">打赏金额：<strong>{{ finalAmount }}</strong> 元</div>
            <div class="qr-method" :class="paymentMethod">
              <i :class="paymentMethod === 'alipay' ? 'fa-brands fa-alipay' : 'fa-brands fa-weixin'"></i>
              {{ paymentMethod === 'alipay' ? '支付宝扫码付款' : '微信扫码付款' }}
            </div>
            
            <p class="qr-tip-text">
              <i class="fa-solid fa-info-circle"></i> 请扫码支付。支付成功后，请在下方输入交易单号/尾号进行对账申报。
            </p>

            <!-- 扫码后输入对账凭证 -->
            <div class="fields-container qr-fields" style="margin-top: 15px;">
              <div class="field-item">
                <label class="field-label" style="font-weight: 600; text-align: left; display: block; margin-bottom: 5px;">
                  {{ paymentMethod === 'wechat' ? '微信对账凭证' : '支付宝对账凭证' }} <span style="color: #e74c3c;">*</span>
                </label>
                <input 
                  v-model="payId" 
                  type="text" 
                  class="field-input" 
                  :placeholder="paymentMethod === 'wechat' ? '输入微信号/微信账单单号/尾号后4位' : '输入支付宝账号/支付宝账单流水号/尾号后4位'" 
                  maxlength="30"
                />
              </div>
            </div>
          </div>

          <div class="action-row">
            <button class="cancel-btn" @click="backToInput">返回修改</button>
            <button 
              class="confirm-btn" 
              :disabled="!payId || !payId.trim()"
              @click="submitCompleted"
            >
              <i class="fa-solid fa-paper-plane"></i>
              提交申报并关闭
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
  </Teleport>
</template>

<script>
import axios from 'axios';
import { message, messageBox } from '@/utils/ui-service';
import authService from '@/services/authService';
import { BaseServerUrl } from '@/assets/BaseServerUrl';

export default {
  name: 'DonationModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      amount: 5,
      presetAmounts: [2, 5, 10, 50],
      customActive: false,
      customAmount: null,
      paymentMethod: 'alipay',
      message: '',
      payId: '',
      isSubmitting: false,
      qrCodeUrl: '',
      donationId: ''
    };
  },
  computed: {
    finalAmount() {
      return this.customActive ? parseFloat(this.customAmount) : this.amount;
    },
    isValidAmount() {
      const val = this.finalAmount;
      return !isNaN(val) && val >= 0.1;
    },
    renderedQrUrl() {
      if (!this.qrCodeUrl) return '';
      if (this.qrCodeUrl.startsWith('http') || this.qrCodeUrl.startsWith('/')) {
        if (this.qrCodeUrl.startsWith('http')) return this.qrCodeUrl;
        return `${BaseServerUrl}${this.qrCodeUrl}`;
      }
      return `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(this.qrCodeUrl)}`;
    }
  },
  methods: {
    close() {
      this.cleanup();
      this.$emit('update:visible', false);
      this.$emit('close');
    },
    cleanup() {
      this.qrCodeUrl = '';
      this.donationId = '';
      this.payId = '';
    },
    selectAmount(amt) {
      this.amount = amt;
      this.customActive = false;
    },
    selectCustom() {
      this.customActive = true;
    },
    backToInput() {
      this.cleanup();
    },
    async createDonation() {
      if (!authService.isLoggedIn()) {
        try {
          await messageBox.confirm(
            '您当前尚未登录。如果您直接打赏，在网站“荣誉墙/赞赏榜”上将展示为“游客”。是否先去登录账号？',
            '提示',
            {
              confirmButtonText: '登录账号',
              cancelButtonText: '作为游客继续',
              type: 'info'
            }
          );
          // 用户选择“登录账号”
          this.close();
          const eventBus = (await import('@/utils/eventBus')).default;
          eventBus.emit('show-login');
          return;
        } catch (cancel) {
          // 用户选择“作为游客继续”，不做任何拦截
        }
      }

      this.isSubmitting = true;
      try {
        const token = localStorage.getItem('token') || '';
        const res = await axios.post(`${BaseServerUrl}/api/donations/create`, {
          amount: this.finalAmount,
          paymentMethod: this.paymentMethod,
          message: this.message
          // 逻辑修改：此时不提交 payId，改为在用户扫码支付后在第二阶段提交
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (res.data && res.data.success) {
          this.qrCodeUrl = res.data.qrCode;
          this.donationId = res.data.donationId;
        } else {
          message.error(res.data?.error || '获取付款码失败');
        }
      } catch (err) {
        message.error(err.response?.data?.error || '服务请求异常，请稍后再试');
      } finally {
        this.isSubmitting = false;
      }
    },
    async submitCompleted() {
      if (!this.payId || !this.payId.trim()) {
        message.warning('请输入付款对账凭证/单号');
        return;
      }
      try {
        const token = localStorage.getItem('token') || '';
        const res = await axios.post(`${BaseServerUrl}/api/donations/submit-verification`, {
          donationId: this.donationId,
          payId: this.payId,
          amount: this.finalAmount,
          paymentMethod: this.paymentMethod,
          message: this.message
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (res.data && res.data.success) {
          message.success('打赏对账申报已提交，等待管理员审核，谢谢！');
          this.close();
        } else {
          message.error(res.data?.error || '提交对账申报失败');
        }
      } catch (err) {
        message.error(err.response?.data?.error || '提交对账申报异常，请稍后再试');
      }
    }
  },
  beforeUnmount() {
    this.cleanup();
  }
};
</script>

<style scoped>
.donation-modal-container {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.donation-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(8px);
}

.donation-modal {
  position: relative;
  z-index: 2001;
  width: 90%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.5);
  display: flex;
  flex-direction: column;
}

body.dark-theme .donation-modal {
  background: #1e1e1e;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
}

.modal-header {
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
}

body.dark-theme .modal-header {
  border-bottom: 1px solid #2d2d2d;
}

.modal-title {
  margin: 0;
  font-size: 1.25em;
  font-weight: 600;
  color: #1d1d1f;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: '爱点风雅黑', sans-serif;
}

body.dark-theme .modal-title {
  color: #f5f5f7;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 1.2em;
  color: #86868b;
  cursor: pointer;
  padding: 4px;
}

.close-btn:hover {
  color: #1d1d1f;
}

body.dark-theme .close-btn:hover {
  color: #f5f5f7;
}

.modal-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.desc-text {
  font-size: 0.9em;
  color: #86868b;
  line-height: 1.5;
  margin: 0;
}

.amount-title, .pay-title {
  font-size: 0.95em;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 2px;
}

body.dark-theme .amount-title,
body.dark-theme .pay-title {
  color: #f5f5f7;
}

.amount-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

.amount-card {
  padding: 10px 4px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  background: transparent;
  color: #1d1d1f;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9em;
  text-align: center;
}

body.dark-theme .amount-card {
  border-color: #3d3d3d;
  color: #eaeaea;
}

.amount-card:hover {
  border-color: #0071e3;
  color: #0071e3;
}

body.dark-theme .amount-card:hover {
  border-color: #7aa2f7;
  color: #7aa2f7;
}

.amount-card.active {
  background: rgba(0, 113, 227, 0.08);
  border-color: #0071e3;
  color: #0071e3;
}

body.dark-theme .amount-card.active {
  background: rgba(122, 162, 247, 0.12);
  border-color: #7aa2f7;
  color: #7aa2f7;
}

.custom-input-container {
  display: flex;
}

.custom-input {
  width: 100%;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  background: transparent;
  color: #1d1d1f;
  outline: none;
}

body.dark-theme .custom-input {
  border-color: #3d3d3d;
  color: #eaeaea;
}

.custom-input:focus {
  border-color: #0071e3;
}

body.dark-theme .custom-input:focus {
  border-color: #7aa2f7;
}

.pay-methods {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pay-btn {
  padding: 12px;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  background: transparent;
  color: #1d1d1f;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  transition: all 0.2s ease;
}

body.dark-theme .pay-btn {
  border-color: #3d3d3d;
  color: #eaeaea;
}

.pay-btn i {
  font-size: 1.3em;
}

.pay-btn.alipay.active {
  background: rgba(0, 113, 227, 0.08);
  border-color: #0071e3;
  color: #0071e3;
}

.pay-btn.wechat.active {
  background: rgba(46, 204, 113, 0.08);
  border-color: #2ecc71;
  color: #2ecc71;
}

body.dark-theme .pay-btn.alipay.active {
  background: rgba(122, 162, 247, 0.12);
  border-color: #7aa2f7;
  color: #7aa2f7;
}

.fields-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 0.85em;
  color: #86868b;
  text-align: left;
}

.field-input {
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: transparent;
  color: #1d1d1f;
  outline: none;
}

body.dark-theme .field-input {
  border-color: #3d3d3d;
  color: #eaeaea;
}

.field-input:focus {
  border-color: #0071e3;
}

body.dark-theme .field-input:focus {
  border-color: #7aa2f7;
}

.action-row {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 10px;
}

.cancel-btn {
  padding: 10px 18px;
  border-radius: 10px;
  border: none;
  background: #f5f5f7;
  color: #1d1d1f;
  cursor: pointer;
  font-weight: 600;
}

body.dark-theme .cancel-btn {
  background: #2d2d2d;
  color: #f5f5f7;
}

.confirm-btn {
  padding: 10px 18px;
  border-radius: 10px;
  border: none;
  background: #0071e3;
  color: #fff;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

body.dark-theme .confirm-btn {
  background: #7aa2f7;
}

.confirm-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 二码展示区 */
.qr-body {
  align-items: center;
  text-align: center;
}

.qr-container {
  width: 200px;
  height: 200px;
  border-radius: 12px;
  padding: 10px;
  background: #fff;
  border: 1px solid #e0e0e0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
}

.qr-image {
  max-width: 100%;
  max-height: 100%;
}

.qr-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qr-amount {
  font-size: 1.1em;
}

.qr-amount strong {
  font-size: 1.4em;
  color: #0071e3;
}

body.dark-theme .qr-amount strong {
  color: #7aa2f7;
}

.qr-method {
  font-weight: 600;
  font-size: 1.05em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.qr-method.alipay { color: #0071e3; }
.qr-method.wechat { color: #2ecc71; }

.qr-tip-text {
  font-size: 0.85em;
  color: #86868b;
  margin: 4px 0 0 0;
  line-height: 1.4;
}

/* Modal Fade 动画 */
.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.25s ease;
}
.modal-fade-enter-from, .modal-fade-leave-to {
  opacity: 0;
}
</style>
